import { useState, useEffect, useCallback } from 'react';

function ScrollDebugBanner() {
  const [enabled, setEnabled] = useState(false);
  const [info, setInfo] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setEnabled(params.has('debugScroll'));
  }, []);

  const capture = useCallback(() => {
    if (!enabled) return;
    const cs = (el) => (el ? getComputedStyle(el) : null);
    const rootEl = document.getElementById('root');

    const bodyS = cs(document.body);
    const htmlS = cs(document.documentElement);
    const rootS = cs(rootEl);

    // ── S1: Base state ──
    const d = {
      bodyOvY: bodyS?.overflowY,
      bodyPos: bodyS?.position,
      htmlOvY: htmlS?.overflowY,
      rootOvY: rootS?.overflowY,
      rootPos: rootS?.position,
      rootH: rootEl ? Math.round(rootEl.getBoundingClientRect().height) : '-',
      innerH: window.innerHeight,
      docH: document.documentElement.clientHeight,
    };

    // ── S2: Scroll owner candidates ──
    const candidates = [];
    const seen = new Set();
    const describe = (el) => {
      const tag = el.tagName?.toLowerCase() || '?';
      const id = el.id ? '#' + el.id : '';
      const cls = el.className && typeof el.className === 'string'
        ? '.' + el.className.trim().split(/\s+/)[0]
        : '';
      return `${tag}${id}${cls}`;
    };
    const tryCandidate = (el) => {
      if (!el || seen.has(el) || el === document.documentElement) return;
      seen.add(el);
      const s = cs(el);
      if (!s) return;
      const ovY = s.overflowY;
      const sH = el.scrollHeight;
      const cH = el.clientHeight;
      const scrollable = sH > cH && (ovY === 'auto' || ovY === 'scroll');
      candidates.push({
        label: describe(el),
        ovY,
        pos: s.position,
        h: Math.round(el.getBoundingClientRect().height),
        cH,
        sH,
        scrollable,
      });
    };

    // Priority scan: body → #root → known wrappers → broad scan
    tryCandidate(document.body);
    if (rootEl) tryCandidate(rootEl);

    const selectors = [
      '[data-layout]', '[data-shell]', '[data-scroll-owner]',
      'main', '.layout', '.app-layout', '.app', '.container',
      '.page', '.content', '.main-content', '.page-content',
    ];
    for (const sel of selectors) {
      const el = document.querySelector(sel);
      if (el) tryCandidate(el);
    }

    // Broad scan (max 200 elements, stop at 3 scrollable)
    if (candidates.filter(c => c.scrollable).length < 3) {
      const all = document.querySelectorAll('*');
      const limit = Math.min(all.length, 200);
      for (let i = 0; i < limit; i++) {
        if (candidates.filter(c => c.scrollable).length >= 3) break;
        tryCandidate(all[i]);
      }
    }

    // Keep top 3: prefer scrollable, then largest scrollHeight
    candidates.sort((a, b) => (b.scrollable - a.scrollable) || (b.sH - a.sH));
    d.candidates = candidates.slice(0, 3);

    // ── S2: elementFromPoint chain (overlay detection) ──
    const cx = Math.round(window.innerWidth / 2);
    const cy = Math.round(window.innerHeight / 2);
    const chain = [];
    // Temporarily make our banner invisible to elementFromPoint
    const bannerEl = document.querySelector('[data-testid="scroll-debug-banner"]');
    if (bannerEl) bannerEl.style.display = 'none';
    let topEl = document.elementFromPoint(cx, cy);
    if (bannerEl) bannerEl.style.display = '';
    const chainSeen = new Set();
    let cur = topEl;
    for (let i = 0; i < 6 && cur && cur !== document.documentElement && cur !== document.body; i++) {
      if (chainSeen.has(cur)) break;
      chainSeen.add(cur);
      const s = cs(cur);
      chain.push({
        label: describe(cur),
        pos: s?.position,
        ptr: s?.pointerEvents,
        z: s?.zIndex,
        opacity: s?.opacity,
      });
      cur = cur.parentElement;
    }
    d.chain = chain;

    setInfo(d);
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    capture();
    window.addEventListener('resize', capture);
    // Re-capture periodically to catch route changes and lazy-loaded content
    const interval = setInterval(capture, 2000);
    return () => {
      window.removeEventListener('resize', capture);
      clearInterval(interval);
    };
  }, [enabled, capture]);

  if (!enabled || !info) return null;

  // Format candidates
  const candLines = (info.candidates || []).map((c, i) =>
    `  C${i+1} ${c.label} ovY:${c.ovY} pos:${c.pos} h:${c.h} cH:${c.cH} sH:${c.sH} scroll:${c.scrollable ? 'YES' : 'no'}`
  ).join('\n');

  // Format elementFromPoint chain
  const chainLines = (info.chain || []).map((c, i) =>
    `  E${i} ${c.label} pos:${c.pos} ptr:${c.ptr} z:${c.z} opacity:${c.opacity}`
  ).join('\n');

  return (
    <div
      data-testid="scroll-debug-banner"
      style={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 999999,
        background: 'rgba(0,0,0,0.92)',
        color: '#0f0',
        fontFamily: 'monospace',
        fontSize: 10,
        lineHeight: 1.4,
        padding: '6px 10px',
        pointerEvents: 'none',
        whiteSpace: 'pre',
        maxHeight: '40vh',
        overflowY: 'auto',
      }}
    >
{`[BASE] body ovY:${info.bodyOvY} pos:${info.bodyPos} | html ovY:${info.htmlOvY} | root ovY:${info.rootOvY} pos:${info.rootPos} rootH:${info.rootH} | innerH:${info.innerH} docH:${info.docH}
[CANDIDATES] (${(info.candidates||[]).length} found, top 3)
${candLines || '  (none)'}
[ELEMENT-FROM-POINT chain at center ${Math.round(window.innerWidth/2)},${Math.round(window.innerHeight/2)}]
${chainLines || '  (none)'}`}
    </div>
  );
}

export default ScrollDebugBanner;
